import dayjs from "dayjs";
import { getLabTestById, getLabtestsList } from "../../services/labtestService";
import { resolvePrescriptionReports } from "./labTestWorkflowUtils";
import {
  getOrganizationId,
  getOrganizationName,
} from "../../utils/organizationUtils";
import { getActiveSubscription } from "../../services/subscriptionHistoryService";
import { calcInvoiceGstBreakdown } from "../../components/InvoiceFormShared";

const buildLabTestLineItems = (pendingTests, labTests) => {
  const normalize = (value) => (value || "").toString().trim().toLowerCase();
  const byId = new Map();
  const byName = new Map();

  (labTests || []).forEach((labTest) => {
    if (!labTest) return;
    if (labTest.id) byId.set(labTest.id, labTest);
    const name = labTest.name || labTest.testName;
    if (name) byName.set(normalize(name), labTest);
  });

  return pendingTests.map((pendingTest) => {
    const labTestId = pendingTest.labTestId || pendingTest.testId;
    const labTest =
      (labTestId && byId.get(labTestId)) || byName.get(normalize(pendingTest.testName));

    return {
      testName: pendingTest.testName,
      amount: Number(labTest?.amount) || 0,
      labTestId: labTestId || labTest?.id || null,
    };
  });
};

const resolveGstPercentage = async (user) => {
  try {
    const organizationId = getOrganizationId(user);
    if (!organizationId) return 0;
    const activeSubscription = await getActiveSubscription(organizationId);
    if (
      activeSubscription?.includeGST !== false &&
      activeSubscription?.gstPercentage != null
    ) {
      return Number(activeSubscription.gstPercentage);
    }
  } catch (error) {
    console.warn("[prepareOpdPrescriptionBilling] Could not load GST percentage:", error);
  }
  return 0;
};

/**
 * Prepare lab-test billing draft for review.
 * Does NOT create an invoice — create happens on Bill in InvoiceModal.
 */
export const billOpdPrescription = async (prescription, user) => {
  const reports = await resolvePrescriptionReports(prescription);

  const pendingTests = (prescription.patientLabTests || []).filter(
    (test) => test.resultStatus === "PENDING" || test.resultStatus == null
  );
  if (pendingTests.length === 0) {
    console.error("No pending tests found for this prescription.");
    return null;
  }

  const normalize = (value) => (value || "").toString().trim().toLowerCase();
  const pendingNames = pendingTests.map((test) => normalize(test.testName));

  let candidateReports = reports.filter(
    (report) =>
      pendingNames.includes(normalize(report.testName)) &&
      (report.resultStatus === null || report.resultStatus === "PENDING")
  );

  const prescReports = candidateReports.filter(
    (report) => report.prescriptionId === prescription.id || report.prescriptionId === null
  );
  let matchedReports = prescReports.length > 0 ? prescReports : candidateReports;

  if (matchedReports.length > 0) {
    const prescDate = prescription.createdAt ? dayjs(prescription.createdAt) : null;
    if (prescDate) {
      matchedReports = matchedReports.filter((report) => {
        const reportDate = report.reportDate || report.createdAt || report.date;
        return reportDate ? dayjs(reportDate).isSame(prescDate, "day") : false;
      });
    }
  }

  if (matchedReports.length > 0) {
    const byName = new Map();
    for (const report of matchedReports) {
      const key = normalize(report.testName);
      const current = byName.get(key);
      const reportDate = report.reportDate || report.createdAt || report.date;
      const reportMoment = reportDate ? dayjs(reportDate) : null;
      if (!current) {
        byName.set(key, report);
      } else {
        const currentDate = current.reportDate || current.createdAt || current.date;
        const currentMoment = currentDate ? dayjs(currentDate) : null;
        if (reportMoment && currentMoment && reportMoment.isAfter(currentMoment)) {
          byName.set(key, report);
        }
      }
    }
    matchedReports = Array.from(byName.values());
  }

  if (matchedReports.length === 0) {
    console.error("No matching reports found for pending tests.");
    return null;
  }

  const reportIds = Array.from(new Set(matchedReports.map((report) => report.reportId)));
  const labTestNames = Array.from(new Set(pendingTests.map((test) => test.testName).filter(Boolean)));

  const labTestIdsFromPresc = Array.from(
    new Set(pendingTests.map((test) => test.labTestId || test.testId).filter(Boolean))
  );
  const labTestIdsFromReports = Array.from(
    new Set(matchedReports.map((report) => report.labTestId ?? report.testId).filter(Boolean))
  );
  const labTestIds = labTestIdsFromPresc.length > 0 ? labTestIdsFromPresc : labTestIdsFromReports;

  if (labTestIds.length === 0 && labTestNames.length === 0) {
    console.error("No valid lab test IDs or names found.");
    return null;
  }

  let amount = 0;
  let labTestLineItems = [];
  if (labTestIds.length > 0) {
    const labTests = await Promise.all(labTestIds.map((id) => getLabTestById(id)));
    const invalidTests = (labTests || []).filter((labTest) => {
      if (!labTest) return true;
      const testAmount = Number(labTest.amount || 0);
      const hasSample =
        labTest?.sampleId || (Array.isArray(labTest?.medicineDetails) && labTest.medicineDetails?.length > 0);
      const hasBom = labTest?.bomId || (Array.isArray(labTest?.details) && labTest.details?.length > 0);
      return !(testAmount > 0 && hasSample && hasBom);
    });

    if (invalidTests.length > 0) {
      const invalidNames = invalidTests
        .map((test) => test.name || test.testName || test.category || "Unknown test")
        .filter(Boolean);
      alert(
        `Cannot generate bill for the following test(s) because Price, Sample or Consumables are not set in Pathology Master:\n\n${invalidNames.join(
          ", "
        )}\n\nPlease update these test details in Add New Medicine → Add New Test (Path-Lab) before billing.`
      );
      return null;
    }

    amount = (labTests || []).reduce((sum, labTest) => sum + (Number(labTest?.amount) || 0), 0);
    labTestLineItems = buildLabTestLineItems(pendingTests, labTests);
  } else {
    const allLabTests = await getLabtestsList();
    const byName = new Map((allLabTests || []).map((test) => [normalize(test.name), test]));
    const labTests = labTestNames.map((name) => byName.get(normalize(name))).filter(Boolean);

    const invalidTests = (labTests || []).filter((labTest) => {
      if (!labTest) return true;
      const testAmount = Number(labTest.amount || 0);
      const hasSample =
        labTest?.sampleId || (Array.isArray(labTest?.medicineDetails) && labTest.medicineDetails?.length > 0);
      const hasBom = labTest?.bomId || (Array.isArray(labTest?.details) && labTest.details?.length > 0);
      return !(testAmount > 0 && hasSample && hasBom);
    });

    if (invalidTests.length > 0) {
      const invalidNames = (invalidTests || [])
        .map((test) => test?.name || test?.testName || test?.category || "Unknown test")
        .filter(Boolean);
      alert(
        `Cannot generate bill for the following test(s) because Price, Sample or Consumables are not set in Pathology Master:\n\n${invalidNames.join(
          ", "
        )}\n\nPlease update these test details in Add New Medicine → Add New Test (Path-Lab) before billing.`
      );
      return null;
    }

    amount = (labTests || []).reduce((sum, labTest) => sum + (Number(labTest?.amount) || 0), 0);
    labTestLineItems = buildLabTestLineItems(pendingTests, labTests);
  }

  const gstPercentage = await resolveGstPercentage(user);
  const { sgst, cgst, totalAmount } = calcInvoiceGstBreakdown(amount, gstPercentage);
  const fromName = getOrganizationName(user) || "Aryan Hospital";
  const hospitalOrgId = getOrganizationId(user);

  const testNamesFromPresc = pendingTests.map((test) => test.testName).filter(Boolean);
  const primaryTestName =
    testNamesFromPresc.length > 0 ? testNamesFromPresc[0] : prescription.patientLabTests?.[0]?.testName || "";
  const finalLabTestNames = testNamesFromPresc.length > 0 ? testNamesFromPresc : labTestNames;

  // Draft only — invoice is created when user clicks Bill on Lab Test Invoice modal
  const invoiceDetails = {
    patientId: prescription.patientId,
    patientName: prescription.patientName || null,
    doctorId: prescription.doctorId ?? prescription.doctor_id ?? null,
    appointmentId:
      prescription.appointmentId ?? prescription.appointment_id ?? null,
    prescriptionId: prescription.id ?? prescription.prescriptionId ?? null,
    particulars: "PATHOLOGYTEST",
    invoiceType: "PATHOLOGYTEST",
    fromName,
    toName: prescription.patientName || null,
    amount,
    sgst,
    cgst,
    totalAmount,
    gstPercentage,
    labTestIds,
    organizationId: hospitalOrgId || undefined,
    labTestNames: finalLabTestNames,
    labTestLineItems,
    date: dayjs().toISOString(),
  };

  return {
    invoiceDetails,
    selectedTest: {
      ...prescription,
      reportIds,
      testName: primaryTestName,
      prescriptionId: prescription.id,
      patientId: prescription.patientId,
      patientName: prescription.patientName,
      doctorId: prescription.doctorId,
      doctorName: prescription.doctorName,
      createdAt: prescription.createdAt,
      patientLabTests: pendingTests.map((test) => ({
        testName: test.testName,
        labTestId: test.labTestId || test.testId,
        resultStatus: test.resultStatus || "PENDING",
        result: test.result || null,
        normalRange: test.normalRange || null,
      })),
      reportData: matchedReports.length > 0 ? matchedReports[0] : null,
      reportId: matchedReports.length > 0 ? matchedReports[0].reportId || matchedReports[0].id : null,
    },
  };
};
